import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getPortalGetPortalMerchantsIdGuidQueryKey,
  getPortalGetPortalMerchantsMerchantIdGuidStoresQueryKey,
  getPortalGetPortalMerchantsQueryKey,
  usePortalGetPortalMerchantsIdGuid,
  usePortalGetPortalMerchantsMerchantIdGuidStores,
  usePortalPostPortalMerchantsIdGuidActivate,
  usePortalPostPortalMerchantsIdGuidSuspend,
  usePortalPostPortalMerchantsMerchantIdGuidStores,
} from '@/lib/api/portal/portal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useHasClientRole } from '@/lib/portal-auth/usePortalAuth';

const StoreSchema = z.object({
  storeId: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  shopperStatement: z.string().min(1).max(140),
});
type StoreForm = z.infer<typeof StoreSchema>;

export default function PortalMerchantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canMutate = useHasClientRole('ClientAdmin');
  const queryClient = useQueryClient();
  const [addStoreOpen, setAddStoreOpen] = useState(false);

  const merchant = usePortalGetPortalMerchantsIdGuid(id ?? '', { query: { enabled: !!id } });
  const stores = usePortalGetPortalMerchantsMerchantIdGuidStores(id ?? '', { query: { enabled: !!id } });

  const suspend = usePortalPostPortalMerchantsIdGuidSuspend();
  const activate = usePortalPostPortalMerchantsIdGuidActivate();

  const toggleStatus = async () => {
    if (!id || !merchant.data) return;
    try {
      if (merchant.data.isActive) await suspend.mutateAsync({ id });
      else await activate.mutateAsync({ id });
      await queryClient.invalidateQueries({ queryKey: getPortalGetPortalMerchantsIdGuidQueryKey(id) });
      await queryClient.invalidateQueries({ queryKey: getPortalGetPortalMerchantsQueryKey() });
    } catch {
      toast.error('Could not update merchant status.');
    }
  };

  if (merchant.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading merchant…</div>;
  }
  if (!merchant.data) {
    return (
      <div className="text-sm text-destructive">
        Merchant not found.{' '}
        <Link to="/portal/merchants" className="font-medium text-primary hover:underline">
          Back to list
        </Link>
      </div>
    );
  }

  const m = merchant.data;

  return (
    <section className="space-y-5">
      <Button variant="ghost" size="sm" className="gap-2" onClick={() => navigate('/portal/merchants')}>
        <ArrowLeft className="h-4 w-4" />
        All merchants
      </Button>

      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{m.name}</h1>
          <p className="font-mono text-xs text-muted-foreground">{m.merchantId}</p>
        </div>
        {canMutate && (
          <Button variant="outline" onClick={toggleStatus} disabled={suspend.isPending || activate.isPending}>
            {m.isActive ? 'Suspend' : 'Activate'}
          </Button>
        )}
      </header>

      <Card>
        <CardHeader>
          <CardDescription>Details</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <div className="text-xs text-muted-foreground">Status</div>
            <div className="mt-1">
              {m.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="warning">Suspended</Badge>}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Balance Account</div>
            <div className="mt-1 font-mono text-sm">{m.balanceAccountId || '—'}</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Stores</CardTitle>
            <CardDescription>Physical or logical sales locations under this merchant.</CardDescription>
          </div>
          {canMutate && (
            <Button size="sm" onClick={() => setAddStoreOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Add store
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {stores.isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : !stores.data || stores.data.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No stores yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium">Store ID</th>
                  <th className="p-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stores.data.map((s) => (
                  <tr key={s.id} className="hover:bg-muted/30">
                    <td className="p-3 font-medium">{s.name}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{s.storeId}</td>
                    <td className="p-3">
                      {s.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="warning">Suspended</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {canMutate && id && (
        <AddStoreDialog merchantId={id} open={addStoreOpen} onOpenChange={setAddStoreOpen} />
      )}
    </section>
  );
}

function AddStoreDialog({
  merchantId,
  open,
  onOpenChange,
}: {
  merchantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const mutation = usePortalPostPortalMerchantsMerchantIdGuidStores({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getPortalGetPortalMerchantsMerchantIdGuidStoresQueryKey(merchantId),
        });
        await queryClient.invalidateQueries({ queryKey: getPortalGetPortalMerchantsIdGuidQueryKey(merchantId) });
        toast.success('Store created.');
        onOpenChange(false);
        reset();
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<StoreForm>({
    resolver: zodResolver(StoreSchema),
    defaultValues: { storeId: '', name: '', shopperStatement: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({
        merchantId,
        data: values,
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(status === 409 ? 'A store with that id already exists.' : 'Could not create store.');
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onOpenChange(false))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New store</DialogTitle>
          <DialogDescription>
            A store sits under this merchant. The shopper statement shows on cardholder statements (140 char max).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="storeId">Store ID</Label>
            <Input id="storeId" placeholder="store_001" autoComplete="off" {...register('storeId')} />
            {errors.storeId && <p className="text-xs text-destructive">{errors.storeId.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" placeholder="Main Street Location" {...register('name')} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="shopperStatement">Shopper statement</Label>
            <Input id="shopperStatement" placeholder="DEALERWORKS SALES" {...register('shopperStatement')} />
            {errors.shopperStatement && <p className="text-xs text-destructive">{errors.shopperStatement.message}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create store'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
